import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';

import { TeamApprovalStatus } from '@/components/constants/enums';
import TeamApprovalStatusIcon from '@/components/molecules/TeamApprovalStatusIcon';
import { useOrganizationStore } from '@/store/organization-store';

type ScheduleCardMobileTabletProps = {
	id: string;
	time: string;
	timeBetweenMatches: string | null;
	firstTeam: string;
	secondTeam: string;
	venue?: string;
	status?: 'newTime' | 'newLocation';
	isWeekView?: boolean;
	onClickProposedNew?: (id: string) => void;
	onClickApprove?: (id: string) => void;
	isFinalSchedule?: boolean;
	isPendingSchedule?: boolean;
	firstTeamStatus?: TeamApprovalStatus;
	secondTeamStatus?: TeamApprovalStatus;
	isCompleted?: boolean;
};

export const ScheduleCardMobileTablet = ({
	id,
	time,
	timeBetweenMatches,
	firstTeam,
	secondTeam,
	venue,
	status,
	isWeekView,
	firstTeamStatus,
	secondTeamStatus,
	isFinalSchedule,
	isPendingSchedule,
	isCompleted
}: ScheduleCardMobileTabletProps) => {
	const { teamLeagueSlug } = useOrganizationStore();

	const isFinalsMatchCompleted = isCompleted && isFinalSchedule;

	const teamsColor = isFinalsMatchCompleted ? 'text-gray-lighter' : 'text-blue-500';
	const locationAndTimeColor = isFinalsMatchCompleted ? 'text-muted-blue' : 'text-blue-400';
	const bgBorderCard = isFinalsMatchCompleted ? 'border-gray-200' : 'border-blue-200';
	const bgCard = isFinalsMatchCompleted ? 'bg-soft-white' : 'bg-light-blue';

	const getHeader = () => {
		if (status === 'newTime') return 'New time!';
		if (status === 'newLocation') return 'New location!';
		return null;
	};

	const renderHeader = () =>
		getHeader() && (
			<div className={'inline-block rounded-lg border border-blue-200 px-2 text-xs font-medium text-blue-300 md:text-sm'}>{getHeader()}</div>
		);

	const renderTeams = () => (
		<div className={`flex flex-col justify-center  gap-1 pt-0 text-xs font-semibold ${teamsColor} sm:py-2 md:pt-0`}>
			<div className="flex items-center justify-between  sm:pr-0 md:pb-0">
				<p className={` ${isPendingSchedule ? 'truncate' : ''}  pr-4 sm:pr-0 md:overflow-visible md:whitespace-normal`}>
					{firstTeam} <span className="inline-block pl-1 ">vs.</span>
				</p>

				{isPendingSchedule && firstTeamStatus ? <TeamApprovalStatusIcon status={firstTeamStatus} /> : null}
			</div>

			<div className="flex items-center justify-between">
				<p className={`${isPendingSchedule ? 'truncate' : ''}  md:overflow-visible md:whitespace-normal`}>{secondTeam}</p>

				{isPendingSchedule && secondTeamStatus ? <TeamApprovalStatusIcon status={secondTeamStatus} /> : null}
			</div>
		</div>
	);

	const renderLocation = () => (
		<div className={`${isFinalSchedule ? 'flex' : 'sm:hidden'}  ${locationAndTimeColor} flex items-center py-2 sm:items-start  md:flex`}>
			<FontAwesomeIcon className="h-5 w-3 sm:h-5 sm:w-4" icon={faLocationDot} />
			<span className="pl-2 pr-4 text-xs font-medium ">{venue}</span>
		</div>
	);

	return (
		<Link href={`/team-leagues/${teamLeagueSlug}/matchup/${id}`} className="md:hidden">
			<div
				className={`w-full rounded-lg border ${bgBorderCard} ${bgCard}  ${
					isWeekView
						? isPendingSchedule
							? 'px-4 py-2 sm:w-[88px] sm:p-1 md:w-[160px]'
							: 'px-4 py-2 sm:w-[100px] sm:px-1 sm:pt-1'
						: 'px-4 py-2'
				}`}
			>
				{isPendingSchedule ? renderHeader() : null}
				<div
					className={`flex items-center justify-between text-sm  md:flex ${isPendingSchedule ? 'py-2  sm:hidden ' : 'flex pb-2 pt-0 sm:pb-0 sm:text-[10px] '}`}
				>
					<span className={`font-medium ${isFinalsMatchCompleted ? 'text-gray-lighter' : 'text-blue-600'} `}>{time}</span>
					{timeBetweenMatches ? (
						<span className={`font-normal ${isFinalsMatchCompleted ? 'text-muted-blue' : 'text-blue-300'}`}>{timeBetweenMatches}</span>
					) : null}
				</div>
				{renderTeams()}
				{venue ? renderLocation() : null}
				{isFinalsMatchCompleted ? (
					<div className="flex justify-center rounded-b-[8px] bg-gray-light text-xs text-dark-gray">Complete</div>
				) : null}
			</div>
		</Link>
	);
};
